import { Icon } from "./Icon";
import meldingenArchief from "../data/meldingen-archief.json";

interface Props {
  onOpenBP26Archief: () => void;
  onOpenUS25Archief: () => void;
  onOpenUS26Archief: () => void;
  onOpenCP: () => void;
  onLock: () => void;
}

const ARCHIEF = meldingenArchief as {
  cp_team: { naam: string; afkorting: string }[];
  meldingen: unknown[];
};

const SPRING = "cubic-bezier(0.32,0.72,0,1)";
const FIRST_NAMES = ARCHIEF.cp_team
  .map((p) => `${p.naam.split(" ")[0]} (${p.afkorting})`)
  .join(" & ");

export function DeploymentLauncher({ onOpenBP26Archief, onOpenUS25Archief, onOpenUS26Archief, onOpenCP, onLock }: Props) {
  return (
    <div className="min-h-full safe-top safe-bottom flex flex-col">
      <header className="px-5 pt-6 pb-4 flex items-center justify-between">
        <Eyebrow>NEXXT · INZET</Eyebrow>
        <button
          onClick={onLock}
          aria-label="Vergrendel app"
          className="w-10 h-10 rounded-full bg-ink-850 hover:bg-ink-800 text-zinc-400 hover:text-zinc-200 flex items-center justify-center"
          style={{
            border: "1px solid rgba(255,255,255,0.06)",
            boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.04)",
            transition: `all 400ms ${SPRING}`,
          }}
        >
          <Icon.Lock />
        </button>
      </header>

      <main className="flex-1 px-5 pt-4 max-w-2xl w-full mx-auto">
        <h1
          className="text-zinc-100 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05] mb-3"
          style={{ letterSpacing: "-0.025em" }}
        >
          Pal Mundo.
        </h1>
        <p className="text-zinc-400 text-base leading-relaxed mb-10 max-w-md">
          18 jul 2026 · Almeerderstrand, Almere.{" "}
          <span className="text-zinc-200">CP gereed voor inzet.</span>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <US26ArchiefCard onClick={onOpenUS26Archief} />
          <US25ArchiefCard onClick={onOpenUS25Archief} />
          <ArchiefCard onClick={onOpenBP26Archief} />
          <ActiveCPCard onClick={onOpenCP} />
        </div>

        <p className="text-2xs text-zinc-600 mt-12 text-center tracking-wide">
          Verwerker: Prepared · Operator: NEXXT Productions
        </p>
        <p className="text-2xs text-zinc-700 mt-1.5 text-center italic">
          Vakwerk verdient bewijs.
        </p>
      </main>

    </div>
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="w-1 h-5 bg-accent rounded-full" />
      <span
        className="text-accent text-[10px] font-semibold uppercase"
        style={{ letterSpacing: "0.2em" }}
      >
        {children}
      </span>
    </div>
  );
}

function US26ArchiefCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group relative text-left p-1.5 rounded-[1.75rem] transition-all"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.05)",
        transition: `all 500ms ${SPRING}`,
      }}
    >
      <div
        className="relative p-5 min-h-[200px] flex flex-col rounded-[calc(1.75rem-0.375rem)] bg-ink-925 group-hover:bg-ink-900 transition-colors"
        style={{
          boxShadow:
            "inset 0 1px 0 0 rgba(255,255,255,0.05), 0 1px 2px rgba(0,0,0,0.3)",
          transition: `all 500ms ${SPRING}`,
        }}
      >
        <div
          className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span className="w-1 h-1 rounded-full bg-zinc-500" />
          <span
            className="text-[9px] font-semibold uppercase text-zinc-400"
            style={{ letterSpacing: "0.18em" }}
          >
            Afgerond · 11 jul 2026{/* archief-ok */}
          </span>
        </div>

        <div className="text-zinc-100 text-2xl font-semibold tracking-tight mt-4 leading-none">
          UltraSonic 2026{/* archief-ok */}
        </div>
        <div className="text-zinc-500 text-sm mt-1.5">
          Maarsseveense Plassen · archief{/* archief-ok */}
        </div>

        <div className="mt-5 space-y-1">
          <div
            className="text-[10px] uppercase font-semibold text-zinc-600"
            style={{ letterSpacing: "0.18em" }}
          >
            Kerncijfers
          </div>
          <div className="text-zinc-300 text-xs leading-relaxed">
            44 meldingen · 10.000 bezoekers · rapport zelfde avond
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/[0.04]">
          <span className="text-2xs text-zinc-500 tabular">live gedraaid · CODE GEEL</span>
          <span
            className="inline-flex items-center gap-2 text-zinc-300 group-hover:text-accent text-xs font-medium"
            style={{ transition: `color 400ms ${SPRING}` }}
          >
            Bekijk archief
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.06)",
                transition: `all 400ms ${SPRING}`,
              }}
            >
              <Arrow className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}

function US25ArchiefCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group relative text-left p-1.5 rounded-[1.75rem] transition-all"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.05)",
        transition: `all 500ms ${SPRING}`,
      }}
    >
      <div
        className="relative p-5 min-h-[200px] flex flex-col rounded-[calc(1.75rem-0.375rem)] bg-ink-925 group-hover:bg-ink-900 transition-colors"
        style={{
          boxShadow:
            "inset 0 1px 0 0 rgba(255,255,255,0.05), 0 1px 2px rgba(0,0,0,0.3)",
          transition: `all 500ms ${SPRING}`,
        }}
      >
        <div
          className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span className="w-1 h-1 rounded-full bg-zinc-500" />
          <span
            className="text-[9px] font-semibold uppercase text-zinc-400"
            style={{ letterSpacing: "0.18em" }}
          >
            Afgerond · 12 jul 2025
          </span>
        </div>

        <div className="text-zinc-100 text-2xl font-semibold tracking-tight mt-4 leading-none">
          UltraSonic 2025{/* archief-ok */}
        </div>
        <div className="text-zinc-500 text-sm mt-1.5">
          Maarsseveense Plassen · archief{/* archief-ok */}
        </div>

        <div className="mt-5 space-y-1">
          <div
            className="text-[10px] uppercase font-semibold text-zinc-600"
            style={{ letterSpacing: "0.18em" }}
          >
            Instroom eindstand
          </div>
          <div className="text-zinc-300 text-xs leading-relaxed">
            8.513 bezoekers (laatste tussenstand)
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/[0.04]">
          <span className="text-2xs text-zinc-500 tabular">gereconstrueerd · 86 taken</span>
          <span
            className="inline-flex items-center gap-2 text-zinc-300 group-hover:text-accent text-xs font-medium"
            style={{ transition: `color 400ms ${SPRING}` }}
          >
            Bekijk bewijs
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.06)",
                transition: `all 400ms ${SPRING}`,
              }}
            >
              <Arrow className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}

function ArchiefCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group relative text-left p-1.5 rounded-[1.75rem] transition-all"
      style={{
        background: "rgba(255,255,255,0.025)",
        border: "1px solid rgba(255,255,255,0.05)",
        transition: `all 500ms ${SPRING}`,
      }}
    >
      <div
        className="relative p-5 min-h-[200px] flex flex-col rounded-[calc(1.75rem-0.375rem)] bg-ink-925 group-hover:bg-ink-900 transition-colors"
        style={{
          boxShadow:
            "inset 0 1px 0 0 rgba(255,255,255,0.05), 0 1px 2px rgba(0,0,0,0.3)",
          transition: `all 500ms ${SPRING}`,
        }}
      >
        <div
          className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span className="w-1 h-1 rounded-full bg-zinc-500" />
          <span
            className="text-[9px] font-semibold uppercase text-zinc-400"
            style={{ letterSpacing: "0.18em" }}
          >
            Afgerond · 5 mei{/* archief-ok */}
          </span>
        </div>

        <div className="text-zinc-100 text-2xl font-semibold tracking-tight mt-4 leading-none">
          BP26 — Bevrijdingspop{/* archief-ok */}
        </div>
        <div className="text-zinc-500 text-sm mt-1.5">
          Haarlem 2026 · archief
        </div>

        <div className="mt-5 space-y-1">
          <div
            className="text-[10px] uppercase font-semibold text-zinc-600"
            style={{ letterSpacing: "0.18em" }}
          >
            Inzet
          </div>
          <div className="text-zinc-300 text-xs leading-relaxed">
            {FIRST_NAMES}
          </div>
        </div>

        <div className="flex-1" />

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/[0.04]">
          <span className="text-2xs text-zinc-500 tabular">
            {ARCHIEF.meldingen.length} meldingen
          </span>
          <span
            className="inline-flex items-center gap-2 text-zinc-300 group-hover:text-accent text-xs font-medium"
            style={{ transition: `color 400ms ${SPRING}` }}
          >
            Bekijk bewijs
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                background: "rgba(255,255,255,0.06)",
                transition: `all 400ms ${SPRING}`,
              }}
            >
              <Arrow className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}

function ActiveCPCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="group relative text-left p-1.5 rounded-[1.75rem]"
      style={{
        background:
          "linear-gradient(135deg, rgba(94,234,212,0.08) 0%, rgba(94,234,212,0.02) 60%, transparent 100%)",
        border: "1px solid rgba(94,234,212,0.18)",
        transition: `all 500ms ${SPRING}`,
      }}
    >
      <div
        className="relative p-5 min-h-[200px] flex flex-col rounded-[calc(1.75rem-0.375rem)]"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,18,22,0.6) 0%, rgba(8,10,12,0.4) 100%)",
          boxShadow:
            "inset 0 1px 0 0 rgba(255,255,255,0.04), 0 1px 2px rgba(0,0,0,0.3)",
        }}
      >
        <div
          className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full"
          style={{
            background: "rgba(94,234,212,0.08)",
            border: "1px solid rgba(94,234,212,0.22)",
          }}
        >
          <span className="w-1 h-1 rounded-full bg-accent animate-pulseSoft" />
          <span
            className="text-[9px] font-semibold uppercase text-accent"
            style={{ letterSpacing: "0.18em" }}
          >
            Actief · 18 jul 2026
          </span>
        </div>

        <div className="text-zinc-100 text-2xl font-semibold tracking-tight mt-4 leading-none">
          Pal Mundo · CP
        </div>
        <div className="text-zinc-400 text-sm mt-1.5 leading-snug">
          Almeerderstrand · max 17.500 bezoekers.
          <br />
          <span className="text-zinc-200">Scenario B — 10k bezoekers.</span>
        </div>

        <div className="flex-1" />

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-accent/[0.12]">
          <span className="text-2xs text-zinc-500">
            28 beveiligers · 11 service
          </span>
          <span
            className="inline-flex items-center gap-2 text-accent text-xs font-medium"
            style={{ transition: `all 400ms ${SPRING}` }}
          >
            Open CP
            <span
              className="w-6 h-6 rounded-full flex items-center justify-center bg-accent text-ink-950"
              style={{ transition: `all 400ms ${SPRING}` }}
            >
              <Arrow className="w-3 h-3 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </span>
          </span>
        </div>
      </div>
    </button>
  );
}

function Arrow({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M7 17L17 7M9 7h8v8" />
    </svg>
  );
}

